// Draft pack review (contracts/studio.md §5): the 50 levels rendered with @lumen/puzzle-render, the difficulty curve
// (actual against the engine's targets), the type distribution, the engine's re-verification, answers behind Reveal
// (R), and per-level Re-generate (same type, flags and target). Publish asks for confirmation: the server
// re-verifies every level with the engine, checks the pack follows the last published one, and then the pack is
// immutable. Delete drops the draft.

import { useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { PuzzleView } from "@lumen/puzzle-render";
import { useDeleteDraft, useDraftPack, usePublishPack, useRegenerateLevel, type PublishResult } from "../../api/levelPacks";
import type { AnswerValue, Level, PackStudioMeta } from "../../api/schemas";
import { useCan } from "../../auth/AuthContext";
import { BarChart, DifficultyCurve } from "../../components/charts/charts";
import { Button, Card, CategoryTag, Chip, ConfirmDialog, EmptyState, ErrorState, Loading, PageHeader, Segmented, Stat } from "../../components/ui";
import { formatDateTime } from "../../lib/dates";
import { typeSlug } from "../../lib/format";
import { useHotkey } from "../../lib/hotkeys";
import pages from "../pages.module.css";
import { AnswerText, BackLink, Callout, RoleNote, StudioError } from "../studio/components";
import s from "../studio/studio.module.css";
import l from "./levelpacks.module.css";

const FLAG_TONE: Record<string, string> = { gate: "danger", relief: "success", intro: "info", chapterEnd: "violet" };

export function DraftPackPage() {
  const { track = "", index: indexParam = "0" } = useParams();
  const index = Number(indexParam);
  const q = useDraftPack(track, Number.isInteger(index) ? index : undefined);
  const navigate = useNavigate();
  const canEdit = useCan("studio.edit");
  const publish = usePublishPack();
  const remove = useDeleteDraft();
  const regen = useRegenerateLevel();
  const [reveal, setReveal] = useState(false);
  const [filter, setFilter] = useState("all");
  const [confirm, setConfirm] = useState<null | "publish" | "delete">(null);
  const [published, setPublished] = useState<PublishResult | null>(null);
  const [regenerating, setRegenerating] = useState<number | null>(null);
  useHotkey("r", () => setReveal((v) => !v), Boolean(q.data));

  const title = (
    <span className={pages.packTitle}>
      <CategoryTag category={track} />
      <span>Pack {Number.isInteger(index) ? index : "?"}</span>
      {q.data ? <span className={pages.faint}>· {q.data.pack.chapter.name}</span> : null}
      {published ? <Chip tone="success">published</Chip> : <Chip tone="accent">draft</Chip>}
    </span>
  );
  const back = <BackLink to="/levels">Levels</BackLink>;

  if (published) return <PublishedView track={track} result={published} title={title} back={back} />;
  if (q.isPending) {
    return (
      <>
        <PageHeader title={title} meta={back} />
        <Loading rows={10} />
      </>
    );
  }
  if (q.isError) {
    return (
      <>
        <PageHeader title={title} meta={back} />
        <ErrorState
          error={q.error}
          title={(q.error as { status?: number }).status === 404 ? "No such draft" : "Couldn't load this draft"}
          onRetry={() => void q.refetch()}
        />
        {(q.error as { status?: number }).status === 404 ? (
          <p className={l.faint}>
            It may have been published or deleted. <Link to={`/levels/${track}/${index}`}>Open the published pack</Link> or go back to <Link to="/levels">Levels</Link>.
          </p>
        ) : null}
      </>
    );
  }

  const data = q.data;
  const meta = data.studio as PackStudioMeta | null | undefined;
  const levels = data.pack.levels;
  const stats = meta?.stats ?? null;
  const targets = new Map((stats?.difficultyCurve ?? []).map((c) => [c.n, c.target]));
  const regenerated = new Map<number, number>();
  for (const r of meta?.regenerated ?? []) regenerated.set(r.n, Math.max(regenerated.get(r.n) ?? 0, r.attempt));
  const typeCounts = stats?.types ?? countTypes(levels);
  const canPublish = meta?.canPublish ?? { ok: false, reason: "No Studio metadata." };
  const verified = stats?.verified;
  const avg = levels.reduce((a, lv) => a + lv.spec.difficulty.score, 0) / Math.max(1, levels.length);
  const first = levels[0]?.n ?? 0;
  const last = levels.at(-1)?.n ?? 0;
  const visible = filter === "all" ? levels : levels.filter((lv) => lv.spec.type === filter);

  return (
    <>
      <PageHeader
        title={title}
        meta={
          <>
            {back}
            <span className="mono">
              L{first}–L{last}
            </span>
            <Chip tone="violet">{data.pack.chapter.palette}</Chip>
            {data.pack.generator ? <span className="mono">{data.pack.generator}</span> : null}
            {meta ? (
              <span className="mono" title={meta.sha256}>
                sha256 {meta.sha256.slice(0, 12)}…
              </span>
            ) : null}
            {meta?.jobId ? <Link to={`/studio/jobs/${meta.jobId}`}>job {meta.jobId.slice(0, 8)}</Link> : null}
          </>
        }
        actions={
          <>
            <RoleNote />
            {canEdit ? (
              <>
                <Button
                  variant="danger"
                  onClick={() => {
                    remove.reset();
                    setConfirm("delete");
                  }}
                >
                  Delete draft
                </Button>
                <Button
                  variant="primary"
                  disabled={!canPublish.ok}
                  title={canPublish.reason ?? undefined}
                  onClick={() => {
                    publish.reset();
                    setConfirm("publish");
                  }}
                >
                  Publish pack {index}
                </Button>
              </>
            ) : null}
          </>
        }
      />

      {meta?.stale ? (
        <Callout tone="danger" title="Already published" testId="draft-stale">
          <p>{canPublish.reason}</p>
        </Callout>
      ) : !canPublish.ok ? (
        <Callout tone="warning" title="Not next in line" testId="draft-order">
          <p>{canPublish.reason}</p>
        </Callout>
      ) : null}
      <StudioError error={regen.error} title="Re-generation failed" />

      <Card>
        <div className={l.strip}>
          <Stat label="Levels" value={levels.length} sub={`L${first}–L${last}`} />
          <Stat
            label="Solver-verified"
            value={verified ? `${verified.ok}/${verified.checked}` : "—"}
            sub={verified && verified.ok === verified.checked ? "unique answers, digests ✓" : `${verified?.failed.length ?? 0} failed`}
          />
          <Stat label="Types" value={Object.keys(typeCounts).length} sub={`${stats?.intros.length ?? 0} intro level${stats?.intros.length === 1 ? "" : "s"}`} />
          <Stat label="Avg difficulty" value={avg.toFixed(2)} sub={`${levels.filter((lv) => lv.flags?.includes("gate")).length} gates`} />
          <Stat label="Re-generated" value={regenerated.size} sub="levels replaced" />
          <Stat label="Generated" value={meta?.createdAt ? formatDateTime(meta.createdAt) : "—"} sub={meta?.createdBy ?? "—"} />
        </div>
      </Card>

      <div className={l.twoCol}>
        <Card title="Difficulty curve">
          <DifficultyCurve
            points={levels.map((lv) => ({ n: lv.n, score: lv.spec.difficulty.score, flags: lv.flags ?? [], label: lv.spec.type }))}
            target={targets.size ? (n) => targets.get(n) ?? levels.find((lv) => lv.n === n)?.spec.difficulty.score ?? 0 : undefined}
          />
        </Card>
        <Card title="Type distribution">
          <BarChart
            label="Levels per type"
            valueName="levels"
            labelWidth={132}
            rows={Object.entries(typeCounts)
              .sort((a, b) => b[1] - a[1])
              .map(([t, n]) => ({ key: t, label: typeSlug(t), value: n }))}
          />
          {stats?.intros.length ? (
            <div className={l.mixChips} style={{ marginTop: 12 }}>
              {stats.intros.map((i) => (
                <Chip key={i.n} tone="info">
                  intro {typeSlug(i.type)} · L{i.n}
                </Chip>
              ))}
            </div>
          ) : null}
        </Card>
      </div>

      <div className={s.toolbar}>
        <Segmented
          label="Type"
          value={filter}
          onChange={setFilter}
          options={[{ value: "all", label: `All ${levels.length}` }, ...Object.entries(typeCounts).map(([t, n]) => ({ value: t, label: `${typeSlug(t)} ${n}` }))]}
        />
        <div className={s.toolbarEnd}>
          <Button size="sm" variant={reveal ? "primary" : "secondary"} aria-pressed={reveal} onClick={() => setReveal((v) => !v)} shortcut="R">
            {reveal ? "Hide answers" : "Reveal answers"}
          </Button>
        </div>
      </div>

      {visible.length === 0 ? (
        <EmptyState title="No level of this type" />
      ) : (
        <div className={s.itemGrid} data-testid="draft-levels">
          {visible.map((lv) => (
            <LevelCard
              key={`${lv.n}:${lv.spec.difficulty.score}:${regenerated.get(lv.n) ?? 0}`}
              level={lv}
              answer={data.answers[lv.spec.id] ?? null}
              target={targets.get(lv.n) ?? null}
              reveal={reveal}
              regenerated={regenerated.get(lv.n) ?? 0}
              busy={regen.isPending && regenerating === lv.n}
              disabled={regen.isPending || Boolean(meta?.stale)}
              onRegenerate={
                canEdit
                  ? () => {
                      setRegenerating(lv.n);
                      regen.mutate({ track, index, n: lv.n });
                    }
                  : undefined
              }
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={confirm === "publish"}
        title={`Publish ${track} pack ${index}?`}
        confirmLabel={`Publish pack ${index}`}
        busy={publish.isPending}
        error={publish.error}
        onCancel={() => setConfirm(null)}
        onConfirm={() =>
          publish.mutate(
            { track, index },
            {
              onSuccess: (res) => {
                setConfirm(null);
                setPublished(res);
              },
            },
          )
        }
      >
        <ul className={l.dialogList}>
          <li>
            Levels L{first}–L{last} go to players at once, in the manifest of every client that supports <span className="mono">{data.pack.requires.length}</span> components it
            uses.
          </li>
          <li>
            First the server re-verifies all {levels.length} levels with the engine&rsquo;s solvers and checks {index === 0 ? "that it is the track's first pack" : `that it follows pack ${index - 1}`}.
          </li>
          <li>
            <strong>Published packs are immutable</strong>: no edits, re-generation or deletion afterwards.
          </li>
        </ul>
        {meta ? (
          <p className={`${l.faint} ${l.sha}`}>
            sha256 <span className="mono">{meta.sha256}</span>
          </p>
        ) : null}
      </ConfirmDialog>

      <ConfirmDialog
        open={confirm === "delete"}
        title={`Delete draft ${track} pack ${index}?`}
        confirmLabel="Delete draft"
        tone="danger"
        busy={remove.isPending}
        error={remove.error}
        onCancel={() => setConfirm(null)}
        onConfirm={() => remove.mutate({ track, index }, { onSuccess: () => navigate("/levels") })}
      >
        <p>The 50 levels are dropped. The next &ldquo;Generate next pack&rdquo; builds pack {index} again.</p>
      </ConfirmDialog>
    </>
  );
}

function countTypes(levels: Level[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const lv of levels) out[lv.spec.type] = (out[lv.spec.type] ?? 0) + 1;
  return out;
}

function LevelCard({
  level,
  answer,
  target,
  reveal,
  regenerated,
  busy,
  disabled,
  onRegenerate,
}: {
  level: Level;
  answer: AnswerValue | null;
  target: number | null;
  reveal: boolean;
  regenerated: number;
  busy: boolean;
  disabled: boolean;
  onRegenerate?: (() => void) | undefined;
}) {
  const flags = level.flags ?? [];
  const explanation = (level as { explanation?: string }).explanation;
  return (
    <article
      className={l.levelCard}
      data-testid="draft-level"
      data-n={level.n}
      data-gate={flags.includes("gate") || undefined}
      data-fresh={regenerated > 0 || undefined}
      aria-label={`Level ${level.n}`}
    >
      <header className={l.levelHead}>
        <span className={l.levelN}>L{level.n}</span>
        <span className={l.levelType}>{typeSlug(level.spec.type)}</span>
        {flags.map((f) => (
          <Chip key={f} tone={FLAG_TONE[f] ?? "neutral"}>
            {f}
          </Chip>
        ))}
        <span className={l.levelScore} title="difficulty.score / the engine's target">
          <b>{level.spec.difficulty.score.toFixed(2)}</b>
          {target !== null ? ` / ${target.toFixed(2)}` : ""}
        </span>
      </header>
      <div className={s.itemStage}>
        <PuzzleView spec={level.spec} answer={answer} reveal={reveal} compact />
      </div>
      <div className={s.itemText}>
        <span className={pages.answerLine} style={{ marginTop: 0 }}>
          <span className="monoLabel">Answer</span>
          {reveal ? <AnswerText answer={answer} /> : <span className={pages.hidden}>hidden · press R</span>}
        </span>
        {reveal && explanation ? <p>{explanation}</p> : null}
      </div>
      <div className={l.levelFoot}>
        {regenerated ? <Chip tone="accent">re-generated ×{regenerated}</Chip> : null}
        <span className="mono" style={{ fontSize: 11, color: "var(--color-text-quaternary)" }}>
          {level.spec.id}
        </span>
        {onRegenerate ? (
          <Button size="sm" variant="ghost" busy={busy} disabled={disabled && !busy} onClick={onRegenerate} style={{ marginLeft: "auto" }}>
            Re-generate
          </Button>
        ) : null}
      </div>
    </article>
  );
}

function PublishedView({ track, result, title, back }: { track: string; result: PublishResult; title: ReactNode; back: ReactNode }) {
  const navigate = useNavigate();
  const p = result.pack;
  return (
    <>
      <PageHeader
        title={title}
        meta={back}
        actions={
          <Button variant="primary" onClick={() => navigate(`/levels/${track}/${p.index}`)}>
            Open the published pack
          </Button>
        }
      />
      <Callout tone="success" title={`Published: ${track} pack ${p.index}, levels L${p.first}–L${p.last}`} testId="published">
        <div className={l.published}>
          <p>
            Every level passed the engine&rsquo;s re-verification ({result.verified.ok}/{result.verified.checked}). The manifest lists it now for clients that support{" "}
            <span className="mono">{p.requires.join(", ")}</span>.
          </p>
          <p className="mono">
            {result.manifestEntry.path} · sha256 {p.sha256}
          </p>
          <span className="monoLabel">sha256 per language (manifest and ETag)</span>
          <div className={l.hashes}>
            {Object.entries(result.hashes).map(([lang, sha]) => (
              <span key={lang}>
                {lang} {sha.slice(0, 12)}…
              </span>
            ))}
          </div>
        </div>
      </Callout>
    </>
  );
}
